/**
 * The homepage headliner: a big map of one featured list with a panel over it and the list beneath.
 * Works signed out (public endpoint). `panel` is whatever should float over the map's top-left.
 */
import { useMemo, useState } from "react";
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable, type SortingState } from "@tanstack/react-table";

import { DataTable, DataTableColumnHeader, EmptyValue, PriceCell, RatingCell } from "@/components/data-table";
import { getCuisineLabels, getPrimaryCuisine } from "@/lib/cuisine";
import { CITIES, type CityKey } from "@/constants/cities";
import RestaurantMap from "@/features/dashboard/pages/map/components/restaurant-map";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, MapPin } from "lucide-react";
import { useAuth } from "@/providers/auth.provider";
import { AuthState } from "@/types/auth.types";
import { RestaurantExpandedRow } from "@/features/dashboard/pages/restaurants/components/restaurant-details";
import type { Restaurant } from "@/features/dashboard/pages/restaurants/types/restaurants.types";

import { usePublicList } from "../hooks/use-public-list";
import type { PublicListItem, PublicListSource } from "../services/public-lists.service";

/** Facts only, for visitors who are not signed in (hearts and reviews need an account). */
function PublicExpandedRow({ item }: { item: PublicListItem }) {
  const cuisine = getCuisineLabels(item.types);
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${item.name} ${item.formattedAddress}`)}`;
  return (
    <div className="border-t border-border bg-background px-3 py-3" onClick={(e) => e.stopPropagation()}>
      <div className="grid gap-4 md:grid-cols-2 md:gap-x-8">
        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-2.5">
            <MapPin className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <div>
              <p className="text-[12.5px] text-muted-foreground">Address</p>
              <p className="text-[13.5px]">{item.formattedAddress}</p>
            </div>
          </div>
          {item.website && (
            <div className="flex items-start gap-2.5">
              <ExternalLink className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <div>
                <p className="text-[12.5px] text-muted-foreground">Website</p>
                <a href={item.website} target="_blank" rel="noopener noreferrer" className="break-all text-[13.5px] text-brand underline underline-offset-[3px]">
                  {item.website.replace(/^https?:\/\//, "").replace(/\/.*$/, "")}
                </a>
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-3">
          {cuisine.length > 0 && (
            <div>
              <p className="mb-1 text-[12.5px] text-muted-foreground">Cuisine</p>
              <div className="flex flex-wrap gap-1.5">
                {cuisine.map((c) => (
                  <Badge key={c}>{c}</Badge>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm">
              <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
                <MapPin aria-hidden /> Open in Google Maps
              </a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Shape a public list item like a saved restaurant so the signed-in expanded row can render it. */
const toRestaurant = (item: PublicListItem): Restaurant => ({
  ...item,
  placeId: "",
  lists: [],
  createdAt: item.addedAt,
});

const columnHelper = createColumnHelper<PublicListItem>();
const columns = [
  columnHelper.accessor("name", {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
    cell: (info) => <span className="font-semibold text-foreground">{info.getValue()}</span>,
  }),
  columnHelper.accessor((row) => getPrimaryCuisine(row.types) ?? "", {
    id: "cuisine",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Cuisine" />,
    cell: (info) => (info.getValue() ? <span className="text-muted-foreground">{info.getValue()}</span> : <EmptyValue />),
  }),
  columnHelper.accessor("rating", {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Rating" align="right" />,
    cell: (info) => <RatingCell rating={info.getValue()} />,
    sortingFn: (a, b) => parseFloat(a.original.rating || "0") - parseFloat(b.original.rating || "0"),
    meta: { align: "right" },
  }),
  columnHelper.accessor("priceLevel", {
    header: ({ column }) => <DataTableColumnHeader column={column} title="Price" />,
    cell: (info) => <PriceCell priceLevel={info.getValue()} />,
  }),
  columnHelper.accessor("formattedAddress", {
    header: "Address",
    enableSorting: false,
    cell: (info) => <span className="text-muted-foreground">{info.getValue()}</span>,
  }),
];

interface HeadlinerMapProps {
  source: PublicListSource;
  /** Floats over the map's top-left. Omit for a bare map. */
  panel?: React.ReactNode;
  /** Tailwind height classes for the map; defaults to a tall hero. */
  heightClassName?: string;
}

export function HeadlinerMap({ source, panel, heightClassName = "h-[50vh] min-h-[320px] md:h-[70vh]" }: HeadlinerMapProps) {
  const { data: list, isLoading, error } = usePublicList(source);
  const { authState } = useAuth();
  const signedIn = authState === AuthState.AUTHENTICATED;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [center, setCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);

  const items = useMemo(() => list?.items ?? [], [list]);

  const defaultCenter = useMemo(() => {
    const coords = items
      .map((r) => ({ lat: parseFloat(r.latitude), lng: parseFloat(r.longitude) }))
      .filter((c) => !Number.isNaN(c.lat) && !Number.isNaN(c.lng));
    if (coords.length === 0) {
      const city = list?.city ? CITIES[list.city as CityKey] : undefined;
      return city ? { lat: city.lat, lng: city.lng } : CITIES.portland;
    }
    return {
      lat: coords.reduce((s, c) => s + c.lat, 0) / coords.length,
      lng: coords.reduce((s, c) => s + c.lng, 0) / coords.length,
    };
  }, [items, list?.city]);

  const mapRestaurants = useMemo(
    () => items.map((r) => ({ ...r, placeId: "", types: r.types, photos: null })),
    [items]
  );

  const table = useReactTable({
    data: items,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getRowId: (r) => r.id,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const select = (restaurant: { id: string; latitude: string; longitude: string }) => {
    const next = selectedId === restaurant.id ? null : restaurant.id;
    setSelectedId(next);
    if (next) setCenter({ lat: parseFloat(restaurant.latitude), lng: parseFloat(restaurant.longitude) });
    document.getElementById(`row-${restaurant.id}`)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className={`relative w-full overflow-hidden border-y border-foreground ${heightClassName}`}>
        <RestaurantMap
          height="100%"
          center={center || defaultCenter}
          savedRestaurants={mapRestaurants}
          onSavedRestaurantClick={select}
          hoveredId={hoveredId}
          selectedId={selectedId}
          zoom={center ? 15 : 12}
        />
        {panel && (
          <div className="pointer-events-none absolute inset-x-4 top-4 sm:inset-x-auto sm:left-7 sm:max-w-md">
            <div className="pointer-events-auto border border-foreground bg-background/95 p-5 shadow-[0_8px_24px_rgba(0,0,0,0.08)] backdrop-blur-sm sm:p-6">
              {panel}
            </div>
          </div>
        )}
      </div>

      <section className="mx-auto flex w-full max-w-[1280px] flex-col gap-3 px-4 sm:px-7">
        <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
          <h2 className="font-display text-[28px] font-medium leading-[1.05] tracking-[-0.02em] text-brand">
            {list?.listName ?? "Featured list"}
          </h2>
          {list && (
            <p className="text-[13.5px] text-muted-foreground">
              {items.length} {items.length === 1 ? "place" : "places"}
              {list.creatorName ? `, by ${list.creatorName}` : ""}
            </p>
          )}
        </div>
        {error ? (
          <p className="text-[13.5px] text-muted-foreground">
            {/not found/i.test(error.message)
              ? "This list isn't available. The link may have been turned off."
              : "Could not load this list right now."}
          </p>
        ) : (
          <DataTable
            table={table}
            isLoading={isLoading}
            emptyMessage="This list is empty."
            selectedRowId={selectedId}
            hoveredRowId={hoveredId}
            onRowClick={select}
            onRowHoverChange={(r) => setHoveredId(r?.id ?? null)}
            expandedRowId={selectedId}
            renderExpanded={(item) =>
              signedIn ? <RestaurantExpandedRow restaurant={toRestaurant(item)} hideRemove /> : <PublicExpandedRow item={item} />
            }
          />
        )}
      </section>
    </div>
  );
}
